/**
 * Calendar utilities for generating iCalendar data and calendar links
 */

/**
 * Generate an iCalendar (.ics) file content for an interview
 * @param {Object} interview - The interview object
 * @param {string} userTimeZone - User's timezone (e.g. 'America/New_York')
 * @returns {string} - iCalendar formatted string
 */
export const generateICSContent = (interview, userTimeZone = 'UTC') => {
  const start = new Date(interview.scheduled_at);
  
  // Calculate end time based on duration (default to 1 hour if not specified)
  const durationMinutes = interview.duration_minutes || 60;
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  
  // Format dates for iCalendar (YYYYMMDDTHHMMSSZ)
  const formatDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '').split('T').join('T');
  };
  
  const startFormatted = formatDate(start);
  const endFormatted = formatDate(end);
  const now = formatDate(new Date());
  
  let location = '';
  if (interview.type === 'video') {
    location = interview.meeting_link || 'Video conference link will be provided';
  } else if (interview.type === 'phone') {
    location = `Phone call to ${interview.candidate_phone || 'candidate'}`;
  } else {
    location = interview.location || 'Location to be determined';
  }

  // Generate a unique ID for the event
  const uid = `interview-${interview.id}-${now}`;
  
  let description = `Interview with ${interview.candidate_name} for ${interview.job_title}.`;
  if (interview.notes) {
    description += `\n\nAdditional Notes: ${interview.notes}`;
  }
  
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//VA Sprouts//Interview Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${startFormatted}`,
    `DTEND:${endFormatted}`,
    `SUMMARY:Interview with ${interview.candidate_name}`,
    `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
    `LOCATION:${location}`,
    'STATUS:CONFIRMED',
    'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
};

/**
 * Generate Google Calendar URL for an interview
 * @param {Object} interview - The interview object
 * @returns {string} - Google Calendar event URL
 */
export const getGoogleCalendarUrl = (interview) => {
  const start = new Date(interview.scheduled_at);
  const durationMinutes = interview.duration_minutes || 60;
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  
  // Format dates as ISO strings and encode for URL
  const startEncoded = encodeURIComponent(start.toISOString());
  const endEncoded = encodeURIComponent(end.toISOString());
  
  let location = '';
  if (interview.type === 'video') {
    location = interview.meeting_link || 'Video conference';
  } else if (interview.type === 'phone') {
    location = 'Phone call';
  } else {
    location = interview.location || 'In person';
  }
  
  const title = encodeURIComponent(`Interview with ${interview.candidate_name}`);
  const description = encodeURIComponent(`Interview with ${interview.candidate_name} for ${interview.job_title}. ${interview.notes || ''}`);
  
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startEncoded.replace(/[-:.]/g, '')}/${endEncoded.replace(/[-:.]/g, '')}&details=${description}&location=${encodeURIComponent(location)}`;
};

/**
 * Generate Outlook/Microsoft 365 Calendar URL for an interview
 * @param {Object} interview - The interview object
 * @returns {string} - Outlook Calendar event URL
 */
export const getOutlookCalendarUrl = (interview) => {
  const start = new Date(interview.scheduled_at);
  const durationMinutes = interview.duration_minutes || 60;
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  
  // Format dates for Outlook (YYYY-MM-DDTHH:MM:SS)
  const formatDate = (date) => {
    return date.toISOString().split('.')[0];
  };
  
  const startEncoded = encodeURIComponent(formatDate(start));
  const endEncoded = encodeURIComponent(formatDate(end));
  
  const title = encodeURIComponent(`Interview with ${interview.candidate_name}`);
  const description = encodeURIComponent(`Interview with ${interview.candidate_name} for ${interview.job_title}. ${interview.notes || ''}`);
  const location = encodeURIComponent(interview.location || interview.type || 'Interview');
  
  return `https://outlook.office.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startEncoded}&enddt=${endEncoded}&body=${description}&location=${location}`;
};

/**
 * Download an ICS file for the interview
 * @param {Object} interview - The interview object 
 */
export const downloadICSFile = (interview) => {
  const icsContent = generateICSContent(interview);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  
  // Create a temporary link to download the file
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `interview-${interview.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};